import { useContext, useRef } from "react";
import { Box, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RopCard1101DisponibilidadeMediaTaticaoAggregated } from "../../types";
import { formatNumberPercent } from "../../../dashboard/helpers/format-number";

export type AvailabilityAverageTacticalChartProps = {
    data: RopCard1101DisponibilidadeMediaTaticaoAggregated;
    viewMode: "cluster" | "powerPlant";
};

export default function AvailabilityAverageTacticalChart({
    data,
    viewMode,
}: AvailabilityAverageTacticalChartProps) {
    const chartRef = useRef(null);
    const { theme } = useContext(MainContext);

    const chartData =
        viewMode === "cluster"
            ? data.clusterAverages.map((item) => ({
                name: item.clusterName || "Sem Cluster",
                value: item.average * 100,
            }))
            : data.powerPlantAverages.map((item) => ({
                name: item.usinaName,
                value: item.average * 100,
            }));

    const xLabels = chartData.map((item) => item.name);
    const values = chartData.map((item) => item.value);

    const getColorByValue = (value: number) => {
        if (value >= 98) return "#2ecc71"; // Verde
        if (value >= 95) return "#f39c12"; // Amarelo/Laranja
        return "#e74c3c"; // Vermelho
    };

    const dataWithColors = values.map((value) => {
        const color = getColorByValue(value);
        return {
            value,
            itemStyle: {
                color: color,
                borderRadius: [6, 6, 0, 0],
            },
            label: {
                color: color,
                fontSize: 12,
            },
        };
    });

    const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: {
            left: "8%",
            right: "8%",
            bottom: "20%",
            top: "8%",
            containLabel: false,
        },
        xAxis: {
            type: "category",
            data: xLabels,
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                color: theme.secondaryTextColor || "#999",
                fontSize: 13,
                margin: 12,
                rotate: 90,
                interval: 0,
            },
        },
        yAxis: {
            type: "value",
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: false,
            },
            axisLabel: {
                color: theme.secondaryTextColor || "#999",
                fontSize: 12,
                margin: 8,
                formatter: "{value}%",
            },
            max: 100,
        },
        series: [
            {
                data: dataWithColors,
                type: "bar",
                barWidth: "80%",
                //barCategoryGap: "50%",
                label: {
                    show: true,
                    position: "top",
                    fontSize: 12,
                    fontWeight: "bold",
                    formatter: (params: any) => {
                        if (params.value !== undefined) {
                            return `${params.value.toFixed(2)}%`;
                        }
                        return "";
                    },
                    distance: 8,
                    rotate: 30,
                },
            },
        ],
        tooltip: {
            trigger: "axis",
            backgroundColor: theme.backgroundColor || "#1a1a1a",
            borderColor: theme.cardBorderColor || "#444",
            textStyle: {
                color: theme.primaryTextColor || "#fff",
            },
            formatter: (params: any) => {
                if (Array.isArray(params) && params[0]) {
                    return `${params[0].name}<br/>Disponibilidade: ${params[0].value.toFixed(2)}%`;
                }
                return "";
            },
        },
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                padding: 2,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 2 }}>
                <AccessTimeOutlinedIcon sx={{ color: "#B980F3", fontSize: 20 }} />
                <Typography
                    sx={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: theme.primaryTextColor,
                    }}
                >
                    Disponibilidade Média
                </Typography>
            </Box>
            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {chartData.length === 0 ? (
                    <Typography
                        sx={{
                            color: theme.secondaryTextColor,
                            fontSize: "14px",
                            fontStyle: "italic",
                        }}
                    >
                        Não há {viewMode === "cluster" ? "clusters" : "usinas"} para serem mostrados
                    </Typography>
                ) : (
                    <RopChart option={option} />
                )}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 2,
                    paddingTop: 2,
                    borderTop: `1px solid ${theme.cardBorderColor}`,
                }}
            >
                <Typography
                    sx={{
                        color: theme.secondaryTextColor,
                        fontWeight: "400",
                        fontSize: "15px",
                    }}
                >
                    Disponibilidade Geral
                </Typography>
                <Typography
                    sx={{
                        color: theme.primaryTextColor,
                        fontWeight: "bold",
                        fontSize: "16px",
                    }}
                >
                    {(formatNumberPercent(data.overallAverage ?? 0, 2, false, false))}%
                </Typography>
            </Box>
        </Box>
    );
}
